import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, LockKeyhole, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  acceptHouseholdInvitation,
  inviteHouseholdMember,
  listHouseholdMembers,
  listManagedHomes,
  listMyHouseholdInvitations,
  removeHouseholdMember,
} from "@/lib/homes.functions";
import { getMyMembership } from "@/lib/memberships.functions";

export const Route = createFileRoute("/_authenticated/home/household")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Household sharing — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HouseholdPage,
});

function HouseholdPage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const client = useQueryClient();
  const homes = useQuery({ queryKey: ["managed-homes"], queryFn: () => listManagedHomes() });
  const membership = useQuery({ queryKey: ["my-membership"], queryFn: () => getMyMembership() });
  const invitations = useQuery({
    queryKey: ["my-household-invitations"],
    queryFn: () => listMyHouseholdInvitations(),
  });
  const ownedHomes = useMemo(
    () => (homes.data ?? []).filter((home: any) => !home.isShared),
    [homes.data],
  );
  const [homeId, setHomeId] = useState("");
  const selectedHomeId = homeId || ownedHomes.find((home: any) => home.id === requestedHomeId)?.id || ownedHomes[0]?.id || "";
  const members = useQuery({
    queryKey: ["household-members", selectedHomeId],
    queryFn: () => listHouseholdMembers({ data: { homeId: selectedHomeId } }),
    enabled: Boolean(selectedHomeId),
  });
  const [busy, setBusy] = useState(false);
  const entitlementKeys = membership.data?.plan?.entitlement_keys ?? [];
  const canShare = entitlementKeys.includes("household_sharing");

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedHomeId) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    try {
      const result = await inviteHouseholdMember({
      data: {
        homeId: selectedHomeId,
        email: String(data.get("email") || ""),
        displayName: String(data.get("name") || "") || undefined,
        role: String(data.get("role") || "member") as "member" | "manager",
        returnUrl: window.location.origin + "/home/household",
      },
    });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      form.reset();
      toast.success(result.emailSent ? "Household invitation sent" : "Invitation created. Email delivery needs attention.");
      await client.invalidateQueries({ queryKey: ["household-members", selectedHomeId] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create household invitation");
    } finally {
      setBusy(false);
    }
  }

  async function accept(inviteId: string) {
    setBusy(true);
    try {
      const result = await acceptHouseholdInvitation({ data: { inviteId } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not accept invitation");
        return;
      }
      toast.success("Household invitation accepted");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["my-household-invitations"] }),
        client.invalidateQueries({ queryKey: ["managed-homes"] }),
        client.invalidateQueries({ queryKey: ["home-documents"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not accept invitation");
    } finally {
      setBusy(false);
    }
  }

  async function remove(memberId: string) {
    setBusy(true);
    try {
      const result = await removeHouseholdMember({ data: { memberId } });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success("Household access removed");
      await client.invalidateQueries({ queryKey: ["household-members", selectedHomeId] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove household access");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>

        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">My Home</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Household Sharing</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Invite people you trust to help manage your property, reminders and shared home information.
          </p>
        </header>

        {(invitations.data ?? []).length > 0 && (
          <section className="mt-7">
            <h2 className="text-lg font-extrabold">Invitations for you</h2>
            <div className="mt-4 space-y-3">
              {(invitations.data ?? []).map((invite: any) => (
                <article key={invite.id} className="card-surface flex flex-wrap items-center justify-between gap-4 p-4">
                  <div>
                    <strong>{invite.saved_homes?.label ?? "Shared home"}</strong>
                    <p className="text-sm text-muted-foreground">
                      {invite.saved_homes?.town_city ?? ""} · {invite.household_role}
                    </p>
                  </div>
                  <Button disabled={busy} onClick={() => accept(invite.id)}>{busy ? "Working…" : "Accept invitation"}</Button>
                </article>
              ))}
            </div>
          </section>
        )}

        {membership.isError || homes.isError || invitations.isError ? (
          <section className="card-surface mt-8 p-5">
            <p>We couldn’t load your household access.</p>
            <Button type="button" variant="outline" className="mt-3" onClick={() => { if (membership.isError) membership.refetch(); if (homes.isError) homes.refetch(); if (invitations.isError) invitations.refetch(); }}>Try again</Button>
          </section>
        ) : membership.isLoading || homes.isLoading || invitations.isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading household access…</p>
        ) : requestedHomeId && !ownedHomes.some((home: any) => home.id === requestedHomeId) ? (
          <section className="card-surface mt-8 p-5"><p>You cannot manage sharing for this property.</p><Link to="/home" className="btn-secondary-hero mt-3 inline-flex">Your properties</Link></section>
        ) : !canShare ? (
          <section className="business-locked mt-8">
            <LockKeyhole />
            <div>
              <h2>Household sharing is included with Shields Family and Complete</h2>
              <p>You can still accept a household invitation from somebody else without upgrading your own plan.</p>
              <Link to="/plans">Compare memberships</Link>
            </div>
          </section>
        ) : (
          <>
            <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="os-module-icon"><UserPlus /></span>
                <div>
                  <h2 className="font-display text-lg font-bold">Invite someone</h2>
                  <p className="text-sm text-muted-foreground">They must sign in using the email address you invite.</p>
                </div>
              </div>

              {ownedHomes.length ? (
                <form onSubmit={invite} className="mt-5 grid gap-4">
                  <label className="grid gap-1.5 text-sm font-semibold">
                    Property
                    <select
                      value={selectedHomeId}
                      onChange={(e) => setHomeId(e.target.value)}
                      className="min-h-11 rounded-md border border-input bg-background px-3"
                    >
                      {ownedHomes.map((home: any) => (
                        <option key={home.id} value={home.id}>{home.label} · {home.postcode}</option>
                      ))}
                    </select>
                  </label>
                  <Input name="name" maxLength={120} placeholder="Name (optional)" />
                  <Input name="email" type="email" required placeholder="Email address" />
                  <label className="grid gap-1.5 text-sm font-semibold">
                    Permission
                    <select name="role" className="min-h-11 rounded-md border border-input bg-background px-3">
                      <option value="member">Member — view shared home information</option>
                      <option value="manager">Manager — help manage the home</option>
                    </select>
                  </label>
                  <Button type="submit" disabled={busy}>{busy ? "Sending…" : "Send Invitation"}</Button>
                </form>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border p-5 text-center">
                  <p className="text-sm text-muted-foreground">Add a property before inviting household members.</p>
                  <Link to="/home" className="btn-secondary-hero mt-4 inline-flex">
                    Add or manage a property
                  </Link>
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-extrabold">People with access</h2>
              </div>
              {members.isLoading ? (
                <p className="mt-4 text-sm text-muted-foreground">Loading household…</p>
              ) : members.isError ? (
                <div className="mt-4 rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground">We couldn’t load household access for this property.</p>
                  <button type="button" onClick={() => members.refetch()} className="btn-secondary-hero mt-3">
                    Try again
                  </button>
                </div>
              ) : (members.data ?? []).length === 0 ? (
                <div className="mt-4 rounded-xl border border-dashed border-border p-4">
                  <p className="text-sm text-muted-foreground">Nobody else has access to this property yet.</p>
                  <p className="mt-1 text-xs text-muted-foreground">Use the invitation form above when you want to share it.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {(members.data ?? []).map((member: any) => (
                    <article key={member.id} className="flex items-center justify-between gap-4 rounded-2xl border border-[#e1e8f0] bg-white p-4">
                      <div className="min-w-0">
                        <strong className="block truncate">{member.display_name || member.email}</strong>
                        <p className="truncate text-sm text-muted-foreground">
                          {member.email} · {member.household_role} · {member.status}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(member.id)}
                        disabled={busy}
                        aria-label={`Remove ${member.display_name || member.email}`}
                        className="rounded-lg p-2 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
